import { Component, effect, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { isNil } from '@core/utils/shared-utils';

import { TagsInputComponent } from '@shared/components/row-edition-form/tags-input/tags-input.component';

export interface UpdatePlanningSpaceConfig {
  planningSpace: {
    id: string;
    name: string;
    description: string;
    tags: string[];
  };
}

export interface UpdatePlanningSpaceOutputData {
  id: string;
  name: string;
  description: string;
  tags: string[];
}

@Component({
  selector: 'app-update-planning-space',
  imports: [TranslatePipe, ReactiveFormsModule, TagsInputComponent],
  templateUrl: './update-planning-space.component.html',
  styles: ``
})
export class UpdatePlanningSpaceComponent {
  private translate = inject(TranslateService);
  private fb = inject(FormBuilder);

  closeModal = output();
  updatePlanningSpaceOutput = output<UpdatePlanningSpaceOutputData>();

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  config = input.required<UpdatePlanningSpaceConfig>();

  form = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    description: [''],
    tags: this.fb.control<string[] | null>([])
  });

  constructor() {
    effect(() => {
      this.currentLang();
      const config = this.config();
      if (config?.planningSpace) {
        this.form.patchValue({
          name: config.planningSpace.name,
          description: config.planningSpace.description,
          tags: config.planningSpace.tags
        });
      }
    });
  }

  onSubmit(): void {
    if (this.form.valid) {
      const formValue = this.form.value;
      const updateData: UpdatePlanningSpaceOutputData = {
        id: this.config().planningSpace.id,
        name: formValue.name || '',
        description: formValue.description || '',
        tags: formValue.tags ?? []
      };

      this.updatePlanningSpaceOutput.emit(updateData);
    }
  }

  onCancel(): void {
    this.closeModal.emit();
  }
}
